/**
 * SearchBar component with debounced search
 */

import React, { useState, useMemo, useRef, useEffect } from "react";
import { debounce } from "../lib/search";
import type { SearchBarProps } from "../types/index";
import CloseIcon from "../icons/CloseIcon";
import SparkleIcon from "../icons/SparkleIcon";
import styles from "./SearchBar.module.css";

const MAX_SUGGESTIONS = 6;
const MIN_SUGGESTION_QUERY_LENGTH = 2;

export default function SearchBar({
  onSearch,
  isLoading = false,
  placeholder = "Search plug-ins, skills, or authors...",
  externalQuery,
  clearToken,
  aiEnabled = false,
  onAskAi,
  suggestions,
  selectedIdentities = [],
  onIdentitiesChange,
}: SearchBarProps) {
  const [query, setQuery] = useState(externalQuery ?? "");
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  // Screen-reader-only confirmation of a chip removal. Backspace removes a chip with no visible
  // focus change, so without this a keyboard/AT user gets no signal that anything happened.
  const [chipAnnouncement, setChipAnnouncement] = useState("");
  const onIdentitiesChangeRef = useRef(onIdentitiesChange);
  onIdentitiesChangeRef.current = onIdentitiesChange;
  const inputRef = useRef<HTMLInputElement>(null);
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;
  // Tracks the last value we reported to the parent so we can distinguish a
  // genuine external reset ("Clear filters") from the parent echoing our own
  // value back on re-render.
  // null = "never reported to parent yet"; avoids colliding with "" as a reset target
  const lastSentToParent = useRef<string | null>(null);
  const debouncedSearch = useMemo(
    () => debounce((q: string) => {
      lastSentToParent.current = q;
      onSearchRef.current(q);
    }, 300),
    [],
  );
  // Mirror `query` in a ref so the externalQuery sync effect can read the latest
  // value without taking `query` as a dependency — depending on it would re-run
  // the sync on every keystroke and stomp characters typed after a debounce.
  const queryRef = useRef(query);
  queryRef.current = query;

  useEffect(() => {
    // Only sync when the parent sends a genuinely new value — not when it
    // echoes back the last value we already reported (which would stomp any
    // characters the user typed after the debounce fired).
    // Also update lastSentToParent so a subsequent external reset to the same
    // value (after a clear cycle) is not permanently suppressed.
    if (
      externalQuery !== undefined &&
      externalQuery !== queryRef.current &&
      externalQuery !== lastSentToParent.current
    ) {
      // Cancel any pending debounce so a stale in-flight value isn't sent to
      // the parent after the reset (e.g. "Clear filters" fires while a 300ms
      // debounce for the old query is still scheduled).
      debouncedSearch.cancel();
      lastSentToParent.current = externalQuery;
      setQuery(externalQuery);
    }
  }, [externalQuery, debouncedSearch]);

  useEffect(() => {
    // clearToken covers the case where the parent resets filter state but
    // externalQuery is already "" (unchanged), so the externalQuery effect
    // above can't fire. Each increment cancels any pending debounce and
    // empties the input without sending an onSearch call to the parent
    // (the parent's Clear-filters handler already called applyFilters("")).
    // Guard against the mount run (token starts at 0 and is only bumped on clear).
    if (!clearToken) return;
    debouncedSearch.cancel();
    lastSentToParent.current = "";
    setQuery("");
  }, [clearToken, debouncedSearch]);


  // Suggestions matching the in-progress query, so the
  // user can pick the exact stored spelling (e.g. "VinSolutions") instead of guessing it.
  // Separators are stripped from both sides before comparing — "Vin Sol" must still find
  // "VinSolutions", which has no space for a literal substring check to land on.
  const matchedSuggestions = useMemo(() => {
    const trimmed = query.trim();
    if (!suggestions || trimmed.length < MIN_SUGGESTION_QUERY_LENGTH) return [];
    const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
    const needle = normalize(trimmed);
    const alreadySelected = new Set(selectedIdentities.map((s) => s.toLowerCase()));
    return suggestions
      .filter(({ name }) => {
        const normalized = normalize(name);
        return normalized !== needle && normalized.includes(needle) && !alreadySelected.has(name.toLowerCase());
      })
      .slice(0, MAX_SUGGESTIONS);
  }, [suggestions, query, selectedIdentities]);

  const showSuggestions = suggestionsOpen && matchedSuggestions.length > 0;

  // Multiple picked author/org chips, OR'd together by
  // the parent via groupByIdentities. Fully CONTROLLED by the parent (selectedIdentities is
  // a prop, not local state) — unlike the free-text query, each pick is a discrete, complete
  // event rather than something needing in-flight debounce responsiveness, so there's no
  // need for this component to keep its own echo-suppressed copy the way query/externalQuery
  // does. This also means a chip survives this component remounting (e.g. navigating to a
  // plugin detail and back), as long as the parent persists it — see CatalogPage's
  // initialIdentities.
  //
  // Adds a chip rather than filling the text box — lets the next pick be typed right away
  // without clearing anything by hand.
  //
  // Also tells the PARENT the free-text query is now "" (not just clearing the local box) —
  // without this, whatever was typed to produce this suggestion (e.g. "katie") stayed live in
  // CatalogPage's searchQuery, silently narrowing results underneath the chip. Invisible at
  // first (the box looked empty), but it got written into the URL by the filter-sync effect,
  // so navigating to a detail page and back restored both the stale typed text AND the chip —
  // and it could keep narrowing away *other* chips picked in the same session (e.g. a later
  // chip whose only match doesn't also satisfy the leftover "katie" text filter).
  const selectSuggestion = (name: string) => {
    const next = selectedIdentities.some((p) => p.toLowerCase() === name.toLowerCase())
      ? selectedIdentities
      : [...selectedIdentities, name];
    onIdentitiesChangeRef.current?.(next);
    setQuery("");
    setSuggestionsOpen(false);
    setHighlightedIndex(-1);
    debouncedSearch.cancel();
    lastSentToParent.current = "";
    onSearchRef.current("");
    inputRef.current?.focus();
  };

  const removeIdentity = (name: string) => {
    onIdentitiesChangeRef.current?.(selectedIdentities.filter((p) => p !== name));
    setChipAnnouncement(`Removed ${name}`);
    inputRef.current?.focus();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setQuery(value);
    setSuggestionsOpen(true);
    setHighlightedIndex(-1);
    debouncedSearch(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Backspace/Delete in an EMPTY box removes the most
    // recent chip — the standard tag-input convention (Gmail recipients, Slack, GitHub labels),
    // so undoing a pick doesn't require reaching for its ×. Only when the box is truly empty:
    // with text present the key edits the text as normal. Key-repeat is ignored so holding
    // Backspace to clear typed text stops at the empty box instead of going on to eat chips.
    if (
      (e.key === "Backspace" || e.key === "Delete") &&
      !e.repeat &&
      query === "" &&
      selectedIdentities.length > 0
    ) {
      e.preventDefault();
      removeIdentity(selectedIdentities[selectedIdentities.length - 1]);
      return;
    }
    if (showSuggestions && e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, matchedSuggestions.length - 1));
      return;
    }
    if (showSuggestions && e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (showSuggestions && e.key === "Escape") {
      setSuggestionsOpen(false);
      setHighlightedIndex(-1);
      return;
    }
    if (e.key === "Enter") {
      if (showSuggestions && highlightedIndex >= 0) {
        selectSuggestion(matchedSuggestions[highlightedIndex].name);
        return;
      }
      setSuggestionsOpen(false);
      debouncedSearch.cancel();
      lastSentToParent.current = query;
      onSearchRef.current(query);
    }
  };

  const handleClear = () => {
    setQuery("");
    setSuggestionsOpen(false);
    setHighlightedIndex(-1);
    lastSentToParent.current = "";
    debouncedSearch.cancel();
    onSearchRef.current("");
    if (selectedIdentities.length > 0) {
      onIdentitiesChangeRef.current?.([]);
    }
    inputRef.current?.focus();
  };

  const hasQuery = query.trim().length > 0;

  const handleAskAi = () => {
    // Empty box: nothing to reason about, so invite input rather than being a dead control.
    if (!hasQuery) {
      inputRef.current?.focus();
      return;
    }
    // Flush any pending debounce and hand the fresh value straight up, so the parent arms AI for
    // exactly what is in the box — not a value up to 300ms stale behind the debounce.
    debouncedSearch.cancel();
    lastSentToParent.current = query;
    onAskAi?.(query);
  };

  return (
    <div className={styles.wrap}>
      <div className={`${styles.bar}${isLoading ? ` ${styles.barLoading}` : ""}`}>
        {selectedIdentities.length > 0 && (
          <ul className={styles.chips}>
            {selectedIdentities.map((name) => (
              <li key={name} className={styles.chip}>
                <span>{name}</span>
                <button
                  type="button"
                  onClick={() => removeIdentity(name)}
                  aria-label={`Remove ${name}`}
                  className={styles.chipRemove}
                >
                  <CloseIcon />
                </button>
              </li>
            ))}
          </ul>
        )}
        <input
          type="text"
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => matchedSuggestions.length > 0 && setSuggestionsOpen(true)}
          onBlur={() => setSuggestionsOpen(false)}
          placeholder={selectedIdentities.length > 0 ? "Add another..." : placeholder}
          aria-label={placeholder}
          ref={inputRef}
          disabled={isLoading}
          className={styles.input}
        />
        {isLoading && <span className={styles.loading}>Loading...</span>}
        {(query || selectedIdentities.length > 0) && !isLoading && (
          <button type="button" onClick={handleClear} aria-label="Clear search" className={styles.clear}>
            <CloseIcon />
          </button>
        )}
        {aiEnabled && !isLoading && (
          <button
            type="button"
            onClick={handleAskAi}
            // The pill lights up (askActive) only once there's a question to reason about; before
            // that it is muted but still focuses the box on click, so it teaches the mode exists.
            className={`${styles.ask}${hasQuery ? ` ${styles.askActive}` : ""}`}
            aria-label={hasQuery ? `Ask AI about "${query.trim()}"` : "Ask AI"}
          >
            <SparkleIcon />
            <span className={styles.askLabel}>Ask AI</span>
            <span className={styles.askArrow} aria-hidden="true">→</span>
          </button>
        )}
      </div>
      <span className={styles.srOnly} aria-live="polite">
        {chipAnnouncement}
      </span>
      {showSuggestions && (
        <ul className={styles.suggestions} role="listbox">
          {matchedSuggestions.map(({ name, kind }, i) => (
            <li key={name}>
              <button
                type="button"
                role="option"
                aria-selected={i === highlightedIndex}
                className={`${styles.suggestion}${i === highlightedIndex ? ` ${styles.suggestionHighlighted}` : ""}`}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setHighlightedIndex(i)}
                onClick={() => selectSuggestion(name)}
              >
                <span className={styles.suggestionName}>{name}</span>
                <span className={`${styles.suggestionKind}${kind === "team" ? ` ${styles.suggestionKindTeam}` : ""}`}>
                  {kind === "team" ? "Team" : "Author"}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {query && <p className={styles.searchingFor}>Searching for "{query}"</p>}
    </div>
  );
}
